import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Mail, Edit, Save, X, Eye, RefreshCw, 
  CheckCircle, AlertCircle, FileText, Send
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { getVersionString } from '../config/version';

const AdminEmailTemplatesPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Default templates
  const defaultTemplates = [
    {
      id: 'partner_welcome',
      name: 'Partner Willkommen',
      subject: 'Willkommen bei Dein Gutachten Direkt!',
      description: 'Wird gesendet wenn ein neuer Partner sich registriert',
      variables: ['{{partner_name}}', '{{company_name}}', '{{login_url}}']
    },
    {
      id: 'partner_trial_reminder_30',
      name: 'Trial Erinnerung (30 Tage)',
      subject: 'Ihre Testphase endet in 30 Tagen',
      description: 'Erinnerung 30 Tage vor Trial-Ablauf',
      variables: ['{{partner_name}}', '{{company_name}}', '{{trial_end_date}}', '{{upgrade_url}}']
    },
    {
      id: 'partner_trial_reminder_7',
      name: 'Trial Erinnerung (7 Tage)',
      subject: 'Ihre Testphase endet in 7 Tagen',
      description: 'Erinnerung 7 Tage vor Trial-Ablauf',
      variables: ['{{partner_name}}', '{{company_name}}', '{{trial_end_date}}', '{{upgrade_url}}']
    },
    {
      id: 'partner_trial_expired',
      name: 'Trial Abgelaufen',
      subject: 'Ihre Testphase ist abgelaufen',
      description: 'Wird gesendet wenn Trial abläuft',
      variables: ['{{partner_name}}', '{{company_name}}', '{{reactivate_url}}']
    },
    {
      id: 'new_case_notification',
      name: 'Neuer Fall',
      subject: 'Neuer Fall eingegangen - {{case_id}}',
      description: 'Benachrichtigung bei neuem Fall',
      variables: ['{{partner_name}}', '{{case_id}}', '{{customer_name}}', '{{case_url}}']
    },
    {
      id: 'chat_notification',
      name: 'Neue Chat-Nachricht',
      subject: 'Neue Nachricht zum Fall {{customer_name}}',
      description: 'Benachrichtigung bei neuer Chat-Nachricht',
      variables: ['{{recipient_name}}', '{{sender_name}}', '{{message_preview}}', '{{case_url}}']
    },
    {
      id: 'payout_ready',
      name: 'Auszahlung bereit',
      subject: 'Ihre Provision ist zur Auszahlung bereit',
      description: 'Benachrichtigung wenn Provision auszahlbar',
      variables: ['{{partner_name}}', '{{amount}}', '{{case_count}}']
    },
    {
      id: 'password_reset',
      name: 'Passwort zurücksetzen',
      subject: 'Passwort zurücksetzen - Dein Gutachten Direkt',
      description: 'E-Mail für Passwort-Reset',
      variables: ['{{user_name}}', '{{reset_url}}']
    },
    {
      id: 'salesperson_invite',
      name: 'Vertriebler Einladung',
      subject: 'Einladung zum Vertriebsteam',
      description: 'Einladung für neue Vertriebler',
      variables: ['{{salesperson_name}}', '{{invite_url}}']
    }
  ];

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadTemplates();
  }, [token, navigate]);

  const loadTemplates = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/email-templates`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        // Merge with defaults
        const merged = defaultTemplates.map(dt => ({
          ...dt,
          ...(data.find(t => t.id === dt.id) || {}),
          isCustomized: data.some(t => t.id === dt.id)
        }));
        setTemplates(merged);
      } else {
        setTemplates(defaultTemplates.map(t => ({ ...t, isCustomized: false })));
      }
    } catch (error) {
      console.error('Load error:', error);
      setTemplates(defaultTemplates.map(t => ({ ...t, isCustomized: false })));
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async () => {
    if (!editingTemplate) return;
    
    setSaving(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/email-templates/${editingTemplate.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            subject: editingTemplate.subject,
            html_content: editingTemplate.html_content
          })
        }
      );
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Template gespeichert' });
        setEditingTemplate(null);
        loadTemplates();
      } else {
        setMessage({ type: 'error', text: 'Fehler beim Speichern' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Verbindungsfehler' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const sendTestEmail = async (templateId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/email-templates/${templateId}/test`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Test-E-Mail gesendet' });
      } else {
        setMessage({ type: 'error', text: 'Fehler beim Senden' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Verbindungsfehler' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-brand-green" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-black/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 md:gap-4">
            <button onClick={() => navigate('/admin')} className="text-white/70 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-base md:text-xl font-bold text-white">E-Mail Templates</h1>
          </div>
          <span className="text-white/50 text-xs md:text-sm hidden sm:block">{getVersionString()}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600" />
            )}
            <span className={message.type === 'success' ? 'text-green-700' : 'text-red-700'}>
              {message.text}
            </span>
          </div>
        )}

        {/* Templates List */}
        <div className="grid gap-4">
          {templates.map(template => (
            <div key={template.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="p-4 md:p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-brand-green/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-brand-green" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{template.name}</h3>
                        {template.isCustomized && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                            Angepasst
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{template.description}</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPreviewTemplate(template)}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingTemplate({ ...template })}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-sm">
                    <span className="text-gray-500">Betreff:</span>
                    <span className="ml-2 text-gray-900">{template.subject}</span>
                  </div>
                </div>
                
                <div className="mt-3 flex flex-wrap gap-2">
                  {template.variables?.map(v => (
                    <code key={v} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                      {v}
                    </code>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Edit Modal */}
        {editingTemplate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Template bearbeiten</h2>
                <button onClick={() => setEditingTemplate(null)} className="text-gray-500 hover:text-gray-700">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Betreff
                  </label>
                  <input
                    type="text"
                    value={editingTemplate.subject}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, subject: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    HTML Inhalt
                  </label>
                  <textarea
                    value={editingTemplate.html_content || ''}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, html_content: e.target.value })}
                    rows={15}
                    placeholder="HTML Template eingeben... Verwenden Sie die Variablen oben."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg font-mono text-sm"
                  />
                </div>
                
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-2">Verfügbare Variablen:</h4>
                  <div className="flex flex-wrap gap-2">
                    {editingTemplate.variables?.map(v => (
                      <code 
                        key={v} 
                        className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded cursor-pointer hover:bg-blue-200"
                        onClick={() => {
                          const textarea = document.querySelector('textarea');
                          const pos = textarea.selectionStart;
                          const text = editingTemplate.html_content || '';
                          setEditingTemplate({
                            ...editingTemplate,
                            html_content: text.slice(0, pos) + v + text.slice(pos)
                          });
                        }}
                      >
                        {v}
                      </code>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <Button variant="outline" onClick={() => setEditingTemplate(null)}>
                  Abbrechen
                </Button>
                <Button
                  className="bg-brand-green hover:bg-brand-green-dark text-white"
                  onClick={saveTemplate}
                  disabled={saving}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Speichern
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEmailTemplatesPage;
